import { DRAWABLE_TYPES, IMUP_JOIST_TYPES_GROUP } from '../constants/drawable-types'
import { DRAWABLE_GROUPPING_NAMES } from '../../components/markup/utils/constants/drawables-sort-order'
import { GeometryGroup, GeometryGroupWithTempType } from '../../imup/slices/geometry'
import { DrawableCoordinate, OpeningGroup } from '../../models/activeDrawable'

export const isHeaderAndBeamGroup = (type: DRAWABLE_TYPES, settings): boolean => {
    return (
        (type?.toLowerCase() === DRAWABLE_TYPES.BEAM && settings.beam_type === 'header') ||
        (type?.toLowerCase() === DRAWABLE_TYPES.AUTOMATED_BEAM_AND_HEADER && !!settings.type)
    )
}

export const isEaveGableGroup = (drawableType: DRAWABLE_TYPES, drawableGroupSettingsName: string): boolean => {
    return (
        (drawableType === DRAWABLE_TYPES.ROOFING && drawableGroupSettingsName?.toLowerCase().includes('eave length')) ||
        (drawableType === DRAWABLE_TYPES.ROOFING &&
            drawableGroupSettingsName?.toLowerCase().includes('gable length')) ||
        drawableType === DRAWABLE_TYPES.EAVE_LENGTH ||
        drawableType === DRAWABLE_TYPES.GABLE_LENGTH
    )
}

export const isFoundationPilonGroup = (drawableType: string): boolean => {
    return drawableType === DRAWABLE_TYPES.FOUNDATION_PILON
}

export const isDeckOrPorchGroup = (drawableGroup): boolean => {
    return (
        drawableGroup.settings.folder?.toLowerCase() === DRAWABLE_GROUPPING_NAMES.DECK ||
        drawableGroup.settings.folder?.toLowerCase() === DRAWABLE_GROUPPING_NAMES.EXTERIOR_DECK ||
        drawableGroup.settings.folder?.toLowerCase()?.includes(DRAWABLE_GROUPPING_NAMES.PORCH) ||
        drawableGroup.type?.toLowerCase() === DRAWABLE_TYPES.LF_FIVE_DOT_FIVE_DECKING ||
        drawableGroup.type?.toLowerCase() === DRAWABLE_TYPES.LF_RAILING
    )
}

export const isJoistGroup = (type: DRAWABLE_TYPES): boolean => {
    return IMUP_JOIST_TYPES_GROUP.includes(type)
}

export const isJoistLine = (c: DrawableCoordinate): boolean => {
    return isJoistGroup(c.drawable.type) && c.coordinates.length === 2
}

export const isFloorTrussSystemGroup = (type: DRAWABLE_TYPES): boolean => {
    return type === DRAWABLE_TYPES.FLOOR_TRUSS_SYSTEM || type === DRAWABLE_TYPES.FLOOR_TRUSS_BRACING
}

export const isPorchCeiling = (type: DRAWABLE_TYPES, folderName: string): boolean => {
    return (
        type?.toLowerCase() === DRAWABLE_TYPES.PORCH_CEILING ||
        (type?.toLowerCase() === DRAWABLE_TYPES.ROOFING && folderName?.toLowerCase()?.includes('porch ceiling'))
    )
}

export const isRoofingExterior = (type: DRAWABLE_TYPES, name: OpeningGroup['settings']['folder'] = ''): boolean => {
    return (
        (type?.toLowerCase() === DRAWABLE_TYPES.ROOFING && name.toLowerCase().includes('exterior')) ||
        type?.toLocaleLowerCase() === DRAWABLE_TYPES.EXTERIOR_ROOFING ||
        type?.toLocaleLowerCase() === DRAWABLE_TYPES.FLASHING ||
        type?.toLocaleLowerCase() === DRAWABLE_TYPES.ROOF_LINE
    )
}

export const isRoofingFraming = (type: DRAWABLE_TYPES, name: OpeningGroup['settings']['folder'] = ''): boolean => {
    return (
        (type?.toLowerCase() === DRAWABLE_TYPES.ROOFING && name.toLowerCase().includes('framing')) ||
        type?.toLowerCase() === DRAWABLE_TYPES.VALLEY_PLATE ||
        type?.toLowerCase() === DRAWABLE_TYPES.UNDER_VALLEY_SHEATHING ||
        type?.toLowerCase() === DRAWABLE_TYPES.FRAMING_ROOFING
    )
}

export const isSidingStarterGroup = (drawableType: string): boolean => {
    return drawableType === DRAWABLE_TYPES.SIDING_STARTER
}

export const isWallGroup = (drawableType: string): boolean => {
    return drawableType === DRAWABLE_TYPES.SHEARWALL
}

export const isRoofFramingBeam = (drawableType: DRAWABLE_TYPES, floorLevel: string | null | undefined): boolean => {
    return drawableType === DRAWABLE_TYPES.BEAM && floorLevel === DRAWABLE_GROUPPING_NAMES['ROOF FRAMING']
}

export const isWildCard = (drawableType: string): boolean => {
    return (
        drawableType === DRAWABLE_TYPES.WILDCARD_AREA ||
        drawableType === DRAWABLE_TYPES.WILDCARD_COUNT ||
        drawableType === DRAWABLE_TYPES.WILDCARD_LINE
    )
}

// define the drawable tempType which will be used later to group drawables
export const addTempTypeToDrawables = (filteredDrawables: GeometryGroup[]): GeometryGroupWithTempType[] => {
    const groupsWithTempType: GeometryGroupWithTempType[] = []

    // this loop is added to add tempType, which is variable to just group the openings
    filteredDrawables.forEach((filteredDrawable: GeometryGroup) => {
        if (isEaveGableGroup(filteredDrawable.type, filteredDrawable.settings.name as string)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.EAVE_GABLE,
            })
        } else if (isFoundationPilonGroup(filteredDrawable.type)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.FOUNDATION_PILON,
            })
        } else if (isJoistGroup(filteredDrawable.type)) {
            // in case, if joist type is selected, add tempType as joist, to group openings
            // under the JOIST group label
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.JOISTS,
            })
        } else if (isRoofFramingBeam(filteredDrawable.type, filteredDrawable.settings?.floor_level)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES['ROOF FRAMING'],
            })
        } else if (isFloorTrussSystemGroup(filteredDrawable.type)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.FLOOR_TRUSS,
            })
        } else if (isPorchCeiling(filteredDrawable.type, filteredDrawable.settings.folder)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.PORCH_CEILING,
            })
        } else if (isDeckOrPorchGroup(filteredDrawable)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.EXTERIOR_DECK,
            })
        } else if (isRoofingExterior(filteredDrawable.type, filteredDrawable.settings?.folder)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.EXTERIOR_ROOFING,
            })
        } else if (isRoofingFraming(filteredDrawable.type, filteredDrawable.settings?.folder)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.FRAMING_ROOFING,
            })
        } else if (isSidingStarterGroup(filteredDrawable.type)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.SIDING_STARTER,
            })
        } else if (isWallGroup(filteredDrawable.type)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_GROUPPING_NAMES.WALL,
            })
        } else if (isWildCard(filteredDrawable.type)) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: filteredDrawable.type,
            })
        } else if (
            filteredDrawable.type === DRAWABLE_TYPES.GARAGE_AREA ||
            filteredDrawable.type === DRAWABLE_TYPES.BASEMENT_FINISHED_CEILING
        ) {
            // Place the garage are under the floor area
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: DRAWABLE_TYPES.FLOOR_AREA.toLowerCase(),
            })
        } else if (filteredDrawable.type === DRAWABLE_TYPES.BEAM) {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: filteredDrawable.settings.beam_type?.toLowerCase(),
            })
        } else {
            groupsWithTempType.push({
                ...filteredDrawable,
                tempType: filteredDrawable.type.toLowerCase(),
            })
        }
    })

    return groupsWithTempType
}
